/**
 * AdminList.tsx — the pieces every Prospera Admin list is built from, lifted
 * from Admin > Lead Sources (LeadSourcesAdmin.tsx), the reference list:
 *
 *   AdminListPage     the page shell: header (title, subtitle, and the page's
 *                     Add button top right in `actions`), the toolbar, then
 *                     the list filling the rest of the height.
 *   AdminBoardPage    the same header and toolbar over a drag-and-drop
 *                     CategoryBoard (Products, Appointment Types), which
 *                     scrolls in its own region and has no pagination.
 *   AdminListToolbar  search (with its icon) on the left, the page's filters
 *                     (ListFilterBar) after it, "Show deleted" on the right.
 *                     It has no slot for buttons on purpose: Add actions go
 *                     in the page header, never inline beside the search.
 *   AdminListTable    the bordered, scrolling, fixed-layout table drawn like
 *                     the original Users list (the shared ui/DataTable):
 *                     text-sm, a sticky slate header with titles as written
 *                     (not uppercase), clickable sortable titles with the
 *                     DataTable sort icons (state from useListSort), cells
 *                     in ADMIN_TD_CLASS, and a right-aligned Actions column
 *                     (ACTIONS_COLUMN).
 *   adminRowClass /   a row's amber wash when deleted, and pointer plus hover
 *   AdminRowName      tint when a click opens it; a name struck through with
 *                     a "Deleted" badge.
 *   AdminListEmpty    the centred "nothing found" message.
 *   PaginationFooter  "Showing X–Y of N things", rows per page, Previous /
 *                     Page x of y / Next (state from useListPagination).
 *
 * Row icon buttons are in AdminRowActions.tsx.
 *
 * On a Locked Admin page (useAdminPageLevel) the page shells leave out their
 * `actions` (the header Add buttons) and a board's drag hint, silently (R18).
 * Search, Show deleted, the filters, sorting and pagination keep working: the
 * toolbar is never inside a field lock.
 */
import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';
import { FieldLockScope } from '@/components/ui/FieldLock';
import { cn, formatCount } from '@/lib/utils';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { PAGE_SIZE_OPTIONS, type ListPaginationState } from './useListPagination';
import type { ListSort } from './useListSort';

// ─── Page shells ───────────────────────────────────────────────────────────────

interface AdminPageHeadingProps {
  title: string;
  subtitle?: ReactNode;
  /** The page's Add button(s): top right of the page, as Lead Sources' "Add Category". */
  actions?: ReactNode;
  /** An AdminListToolbar. */
  toolbar?: ReactNode;
}

/** A paged Admin list: header and toolbar fixed, the table below takes the rest. */
export function AdminListPage({
  title,
  subtitle,
  actions,
  toolbar,
  children,
}: AdminPageHeadingProps & { children: ReactNode }) {
  const locked = useAdminPageLevel() === 'locked';
  return (
    <div className="flex flex-col h-full overflow-hidden p-6">
      <div className="flex-shrink-0">
        <PageHeader title={title} subtitle={subtitle} actions={locked ? undefined : actions} />
        {toolbar}
      </div>
      {children}
    </div>
  );
}

/**
 * A drag-and-drop Admin board: the same header and toolbar, a one-line hint
 * under the toolbar (e.g. why dragging is off), then the board in its own
 * scroll region. `dialogs` are rendered after the scroll region. A Locked
 * board never drags, so it shows no hint.
 */
export function AdminBoardPage({
  title,
  subtitle,
  actions,
  toolbar,
  hint,
  children,
  dialogs,
}: AdminPageHeadingProps & { hint?: ReactNode; children: ReactNode; dialogs?: ReactNode }) {
  const locked = useAdminPageLevel() === 'locked';
  return (
    <div className="flex h-full min-h-0 flex-col overflow-x-hidden max-md:h-auto">
      <div className="shrink-0 p-6">
        <PageHeader title={title} subtitle={subtitle} actions={locked ? undefined : actions} />
        {toolbar}
        {hint && !locked && (
          <p className="mt-2 text-xs text-slate-500" data-testid="board-hint">
            {hint}
          </p>
        )}
      </div>
      <div className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-6 pb-6">{children}</div>
      {dialogs}
    </div>
  );
}

// ─── Toolbar ───────────────────────────────────────────────────────────────────

export interface AdminListToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  /** Accessible name for the search box, e.g. "Search products". */
  searchLabel?: string;
  /** Omit onShowDeletedChange to leave the switch out. */
  showDeleted?: boolean;
  onShowDeletedChange?: (on: boolean) => void;
  /** Id for the switch (its label points at it). */
  showDeletedId?: string;
  /** The switch's label, for a list whose hidden rows are not "deleted" (Forward Merchants: "Show inactive"). */
  showDeletedLabel?: string;
  /** The page's filters — a ListFilterBar. They get their own row under the search. */
  children?: ReactNode;
  className?: string;
}

export function AdminListToolbar({
  search,
  onSearchChange,
  searchPlaceholder,
  searchLabel,
  showDeleted = false,
  onShowDeletedChange,
  showDeletedId = 'show-deleted',
  showDeletedLabel = 'Show deleted',
  children,
  className,
}: AdminListToolbarProps) {
  // Row 1: search, with Show deleted at the far right. Row 2: the filters.
  const toolbar = (
    <div className={cn('mb-4 space-y-3', className)} data-testid="admin-list-toolbar">
      <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[200px] max-w-sm max-md:w-full max-md:flex-none max-md:max-w-none">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchLabel}
          className="pl-9"
        />
      </div>
      {onShowDeletedChange && (
        <div className="flex items-center gap-2 ml-auto">
          <label htmlFor={showDeletedId} className="text-xs text-slate-500 cursor-pointer select-none">
            {showDeletedLabel}
          </label>
          <Switch id={showDeletedId} checked={showDeleted} onChange={onShowDeletedChange} />
        </div>
      )}
      </div>
      {children && (
        <div className="flex flex-wrap items-center gap-3" data-testid="admin-list-filters">
          {children}
        </div>
      )}
    </div>
  );
  // Never field-locked: searching and filtering only read, even on a Locked page.
  return <FieldLockScope locked={false}>{toolbar}</FieldLockScope>;
}

// ─── Table ─────────────────────────────────────────────────────────────────────

export interface AdminListColumn {
  key: string;
  /** Column title, capitalised as written ("Type Name"). Omit for an untitled column such as the expand chevron. */
  header?: ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Width and extra classes, e.g. "w-32" or "w-[30%]". */
  className?: string;
  /**
   * The title sorts the list: it is clickable (and a keyboard button) and
   * shows the sort icon. Needs the table's `onSortChange`; the comparator for
   * `key` goes in the page's useListSort. Never on icon-flag, count-badge or
   * Actions columns.
   */
  sortable?: boolean;
}

/** The original Users list's (ui/DataTable) column title style. */
export const ADMIN_TH_CLASS = 'px-4 py-3 text-xs font-semibold text-slate-600 whitespace-nowrap';

/** Added to a sortable column's title cell. */
export const ADMIN_TH_SORTABLE_CLASS = 'cursor-pointer select-none hover:text-slate-900';

/**
 * The original Users list's (ui/DataTable) body cell. Every <td> in an Admin
 * list starts from it: cn(ADMIN_TD_CLASS, 'w-12 text-center', …) for extra
 * alignment or width. The table itself is text-sm.
 */
export const ADMIN_TD_CLASS = 'px-4 py-3 text-slate-700';

/** The right-aligned Actions column every Admin list ends with. */
export const ACTIONS_COLUMN: AdminListColumn = { key: 'actions', header: 'Actions', align: 'right', className: 'w-32' };

/** DataTable's sort icons: both arrows until the column sorts, then the one arrow in blue. */
function SortIcon({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" data-sort-icon="none" />;
  return dir === 'asc' ? (
    <ChevronUp className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" data-sort-icon="asc" />
  ) : (
    <ChevronDown className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" data-sort-icon="desc" />
  );
}

const ALIGN_CLASS = { left: 'text-left', center: 'text-center', right: 'text-right' } as const;

/**
 * The bordered table: it fills the page's remaining height and scrolls under
 * its sticky header. Pass the rows (<tr>) as children, each cell in
 * ADMIN_TD_CLASS; a PaginationFooter goes right after it (it closes the
 * border underneath).
 *
 * Sorting: mark columns `sortable` and pass `sort` and `onSortChange` from
 * useListSort. The page sorts the rows; the table only draws the titles.
 */
export function AdminListTable({
  columns,
  children,
  sort,
  onSortChange,
  'aria-label': ariaLabel,
}: {
  columns: AdminListColumn[];
  children: ReactNode;
  /** The sort column and direction (undefined while the list keeps its given order). */
  sort?: ListSort;
  /** A sortable title was clicked (useListSort reverses the sort column or switches to this one). */
  onSortChange?: (key: string) => void;
  'aria-label'?: string;
}) {
  return (
    <div className="relative flex-1 min-h-0 border border-slate-200 rounded-t-lg overflow-y-auto">
      <table className="w-full table-fixed text-sm" aria-label={ariaLabel}>
        <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
          <tr>
            {columns.map((c) => {
              if (c.header == null) return <th key={c.key} className={cn(ADMIN_TH_CLASS, c.className)} />;
              const sortable = Boolean(c.sortable && onSortChange);
              const active = sortable && sort?.key === c.key;
              const dir = sort?.dir ?? 'asc';
              return (
                <th
                  key={c.key}
                  scope="col"
                  aria-sort={sortable ? (active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                  className={cn(ALIGN_CLASS[c.align ?? 'left'], ADMIN_TH_CLASS, sortable && ADMIN_TH_SORTABLE_CLASS, c.className)}
                  // The whole cell sorts, as DataTable's; the button inside makes it reachable by keyboard.
                  onClick={sortable ? () => onSortChange?.(c.key) : undefined}
                >
                  {sortable ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {c.header}
                      <SortIcon active={active} dir={dir} />
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">{children}</tbody>
      </table>
    </div>
  );
}

/**
 * A top-level row, as the original Users list's: amber wash when deleted,
 * and a pointer and hover tint when clicking it opens the record.
 */
export function adminRowClass({ deleted = false, clickable = false }: { deleted?: boolean; clickable?: boolean } = {}): string {
  return cn('group transition-colors', clickable && 'cursor-pointer hover:bg-slate-50', deleted && 'bg-amber-50/40');
}

/**
 * A parent (category) row — Patient Notes' colours, the standard for every
 * grouped list and drag-and-drop board: slate-100, slate-200 on hover. The
 * whole row is clickable and toggles its children open and closed.
 */
export const ADMIN_PARENT_ROW_CLASS = 'bg-slate-100 hover:bg-slate-200 cursor-pointer transition-colors';

/**
 * `expandable` false: a parent row with no children — same colour, but no
 * hover or pointer (and the page shows no chevron), so it reads as empty.
 */
export function adminParentRowClass({ deleted = false, expandable = true }: { deleted?: boolean; expandable?: boolean } = {}): string {
  return cn(
    'group',
    expandable ? ADMIN_PARENT_ROW_CLASS : 'bg-slate-100 transition-colors',
    deleted && (expandable ? 'bg-amber-50/60 hover:bg-amber-100/60' : 'bg-amber-50/60'),
  );
}

/** A child row under a parent — Patient Notes' colours: white, slate-50 on hover. */
export const ADMIN_CHILD_ROW_CLASS = 'bg-white hover:bg-slate-50 transition-colors';

export function adminChildRowClass({ deleted = false, clickable = true }: { deleted?: boolean; clickable?: boolean } = {}): string {
  return cn('group bg-white transition-colors', clickable && 'cursor-pointer hover:bg-slate-50', deleted && 'bg-amber-50/40');
}

/**
 * A row's name: struck through with a "Deleted" badge when deleted. `tone`
 * "child" is the smaller, lighter name of a row nested under a category.
 */
export function AdminRowName({
  deleted = false,
  tone = 'parent',
  suffix,
  nameTestId,
  children,
}: {
  deleted?: boolean;
  tone?: 'parent' | 'child';
  /** Shown after the name, before the badge (e.g. the record's #id). */
  suffix?: ReactNode;
  nameTestId?: string;
  children: ReactNode;
}) {
  const live = tone === 'parent' ? 'text-slate-900' : 'text-slate-700';
  return (
    <div className="flex items-center gap-2">
      <span
        data-testid={nameTestId}
        className={cn(tone === 'parent' ? 'font-medium' : 'text-sm', deleted ? 'text-slate-400 line-through' : live)}
      >
        {children}
      </span>
      {suffix}
      {deleted && (
        <Badge variant="warning" size="sm">
          Deleted
        </Badge>
      )}
    </div>
  );
}

/** The centred message shown instead of the table when nothing matches. */
export function AdminListEmpty({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    <div className="text-center py-16 text-slate-500">
      <p className="text-base font-medium text-slate-900 mb-1">{title}</p>
      {hint && <p className="text-sm">{hint}</p>}
    </div>
  );
}

// ─── Pagination footer ─────────────────────────────────────────────────────────

const PAGER_BUTTON =
  'px-2 py-1 text-xs rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed';

/**
 * Lead Sources' footer under the table: "Showing 1–25 of 40 categories", a
 * rows-per-page select (10 / 25 / 50 / 100), and Previous / Page x of y / Next.
 */
export function PaginationFooter({
  pagination,
  noun,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}: {
  pagination: ListPaginationState;
  /** Singular and plural, e.g. ['category', 'categories']. */
  noun: [string, string];
  pageSizeOptions?: readonly number[];
}) {
  const { paged, page, pageSize, initialPageSize, total, totalPages, from, to, setPage, setPageSize } = pagination;
  const countLabel = `${formatCount(total)} ${total === 1 ? noun[0] : noun[1]}`;

  // Just the count, like User Groups, Discounts and Promo Codes: always for a
  // list that shows every row, and for a paged list while every row fits on
  // its opening page. (A user who picked a bigger page size keeps the
  // controls, so they can switch back.)
  if (!paged || (total <= initialPageSize && pageSize === initialPageSize)) {
    return (
      <div
        className="flex-shrink-0 flex items-center border border-t-0 border-slate-200 rounded-b-lg bg-slate-50 px-4 py-2"
        data-testid="pagination-footer"
      >
        <span className="text-xs text-slate-500">{total === 0 ? `No ${noun[1]}` : countLabel}</span>
      </div>
    );
  }

  return (
    <div
      className="flex-shrink-0 flex items-center justify-between border border-t-0 border-slate-200 rounded-b-lg bg-slate-50 px-4 py-2"
      data-testid="pagination-footer"
    >
      <span className="text-xs text-slate-500">
        {total === 0
          ? `No ${noun[1]}`
          : `Showing ${formatCount(from)}–${formatCount(to)} of ${countLabel}`}
      </span>
      <div className="flex items-center gap-2">
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          aria-label="Rows per page"
          className="text-xs border border-slate-200 rounded bg-white px-2 py-1 text-slate-700"
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n} per page
            </option>
          ))}
        </select>
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className={PAGER_BUTTON}>
          Previous
        </button>
        <span className="text-xs text-slate-500">
          Page {page} of {totalPages}
        </span>
        <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)} className={PAGER_BUTTON}>
          Next
        </button>
      </div>
    </div>
  );
}
